import { WhiteBox, FoodsBadge, FoodsIcon, useResponsiveBreakpoints } from "@foodpilot/foods";
import { Stack, Typography, Button, Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanSummaryType } from "src/api/client-api/company/overview/useOrganizationOverviewOneSsq.tsx";
import { DIAGNOSTIC_COLOR_INDEX } from ".";
import { URLHandler } from "src/services/URLHandler";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { Link } from "react-router";

type LeftBlockProps = {
    engagedActions: ActionPlanSummaryType;
};
export const ActionPlanSummaryLayout = (props: LeftBlockProps) => {
    const { engagedActions } = props;

    const theme = useTheme();
    const { t } = useTranslation();
    const { company, ssqVersion } = useCompanyMetadata();

    const { isExtraSmall, isDesktop } = useResponsiveBreakpoints();

    const isVertical = isExtraSmall || isDesktop;

    return (
        <WhiteBox
            sx={{
                gap: theme.spacing(4),
                padding: theme.spacing(4),
                display: "flex",
                height: "100%",
                width: "100%",
                border: `1px solid ${theme.custom.grey[500]}`,
                flexDirection: "column",
                justifyContent: "space-between",
            }}
        >
            <Stack
                flexDirection={isVertical ? "column" : "row"}
                height={"100%"}
                gap={theme.spacing(4)}
                justifyContent={"center"}
                alignItems={"center"}
            >
                <WhiteBox>
                    <Stack
                        padding={theme.spacing(4)}
                        gap={theme.spacing(1)}
                        alignItems={"center"}
                        justifyContent={"center"}
                        height={"100%"}
                    >
                        <FoodsBadge
                            size={4}
                            icon="actionPlan"
                            boxProps={{
                                sx: {
                                    color: theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX].variant[700],
                                    backgroundColor:
                                        theme.custom.dataviz.colorsWithExtra[DIAGNOSTIC_COLOR_INDEX].variant[100],
                                    borderRadius: "8px",
                                },
                            }}
                        />
                        <Typography variant="number-big">{engagedActions.total}</Typography>
                        <Typography variant="big-medium" textAlign="center">
                            {t("action_plan.engaged_actions", "Actions engaged")}
                        </Typography>
                    </Stack>
                </WhiteBox>
                <ActionPlanSummary engagedActions={engagedActions} />
            </Stack>
            <Link to={URLHandler.company.actions.planning(company.id, ssqVersion?.ssq.id ?? -1)}>
                <Button
                    variant="primary"
                    startIcon={<FoodsIcon size={2} icon={"edit"} />}
                    disabled={ssqVersion === undefined}
                    sx={{
                        width: "100%",
                    }}
                >
                    {t("company.manage_action_plan", "Manage my action plan")}
                </Button>
            </Link>
        </WhiteBox>
    );
};

type ActionPlanSummaryProps = {
    engagedActions: ActionPlanSummaryType;
};
const ActionPlanSummary = (props: ActionPlanSummaryProps) => {
    const { engagedActions } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack gap={theme.spacing(2)} padding={theme.spacing(2)}>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <Box
                    sx={{
                        backgroundColor: theme.custom.green[500],
                        width: "12px",
                        height: "12px",
                        borderRadius: "70px",
                    }}
                />
                <Typography>
                    {`${t("action_plan.action_status.completed", "{{completed}} Completed", {
                        completed: engagedActions.byType.completed,
                    })}`}
                </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <Box
                    sx={{
                        backgroundColor: theme.custom.dataviz.colorsWithExtra[10].main,
                        width: "12px",
                        height: "12px",
                        borderRadius: "70px",
                    }}
                />
                <Typography>
                    {`${t("action_plan.action_status.integrated", "{{value}} Integrated", {
                        value: engagedActions.byType.integrated,
                    })}`}
                </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <Box
                    sx={{
                        backgroundColor: theme.custom.dataviz.colorsWithExtra[13].main,
                        width: "12px",
                        height: "12px",
                        borderRadius: "70px",
                    }}
                />
                <Typography>
                    {`${t("action_plan.action_status.ongoing", "{{value}} Currently being deployed", {
                        value: engagedActions.byType.ongoing,
                    })}`}
                </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <Box
                    sx={{
                        backgroundColor: theme.custom.dataviz.colorsWithExtra[5].main,
                        width: "12px",
                        height: "12px",
                        borderRadius: "70px",
                    }}
                />
                <Typography>
                    {`${t("action_plan.action_status.planned", "{{value}} Planned", {
                        value: engagedActions.byType.planned,
                    })}`}
                </Typography>
            </Stack>
            <Stack flexDirection={"row"} gap={theme.spacing(2)} alignItems={"center"}>
                <Box
                    sx={{
                        backgroundColor: theme.custom.dataviz.colorsWithExtra[12].main,
                        width: "12px",
                        height: "12px",
                        borderRadius: "70px",
                    }}
                />
                <Typography>
                    {`${t("action_plan.action_status.considered", "{{value}} Under consideration", {
                        value: engagedActions.byType.considered,
                    })}`}
                </Typography>
            </Stack>
        </Stack>
    );
};
